// comparisonData.js
//
// Two independent pieces live in this file:
//   1) CAPABILITY_CATEGORIES — the 38-row x 10-platform matrix from the
//      original sheet. UNCHANGED — do not edit these values when updating copy.
//   2) COMPETITORS — the per-competitor page copy (hero, quick-pick cards,
//      gaps section, FAQ, meta) from the content doc. This is what drives
//      every section of the page other than the capability table itself.
//
// One template (ComparisonPage.jsx) + this one data file = all 9 pages.
// To add a 10th competitor: add one COMPETITORS entry + one column of
// values in each CAPABILITY_CATEGORIES row.

export const LEVEL_META = {
    full: { label: "Fully supported", glyph: "\u2713" },
    partial: { label: "Partial", glyph: "\u2013" },
    none: { label: "Not a core capability", glyph: "\u2715" },
    undisclosed: { label: "Not disclosed", glyph: "?" },
};

// Column order matches the sheet exactly. NOT changed in this update.
export const COMPETITOR_ORDER = [
    "tractable",
    "ravin",
    "bdeo",
    "tchek",
    "focalx",
    "uveye",
    "weproov",
    "pave",
    "monkai",
];

function row(id, label, values) {
    const keys = ["inspektlabs", ...COMPETITOR_ORDER];
    const entry = { id, label, values: {} };
    keys.forEach((key, i) => {
        entry.values[key] = values[i];
    });
    return entry;
}

// ---------------------------------------------------------------------
// 1) CAPABILITY MATRIX — unchanged from the original sheet.
// values order per row: [inspektlabs, tractable, ravin, bdeo, tchek, focalx, uveye, weproov, pave, monkai]
// ---------------------------------------------------------------------
export const CAPABILITY_CATEGORIES = [
    {
        id: "damage-detection",
        label: "Damage Detection & Accuracy",
        items: [
            row("ai-damage-detection", "AI-powered damage detection (dents, scratches, cracks)",
                ["full", "full", "full", "full", "full", "full", "full", "full", "full", "full"]),
            row("internal-damage", "Detects internal damage (torn seats, dashboard damage)",
                ["full", "full", "full", "full", "full", "full", "full", "partial", "partial", "none"]),
            row("tyre-wheel-rim", "Tyre / wheel / rim condition detection",
                ["full", "partial", "full", "partial", "full", "full", "full", "none", "partial", "partial"]),
            row("damage-severity", "Damage severity classification",
                ["full", "full", "full", "full", "full", "full", "full", "partial", "full", "full"]),
            row("damage-localisation", "Damage localisation on vehicle",
                ["full", "full", "full", "full", "full", "full", "full", "full", "full", "full"]),
            row("glass-specialised-damage", "Glass and specialised damage detection",
                ["full", "full", "partial", "partial", "partial", "partial", "full", "none", "partial", "partial"]),
            row("dashboard-warning-lights", "Dashboard warning lights detection",
                ["full", "none", "partial", "partial", "full", "none", "full", "none", "none", "none"]),
        ],
    },
    {
        id: "capture-methods",
        label: "Capture & Inspection Methods",
        items: [
            row("video-based-inspections", "Video-based inspections",
                ["full", "partial", "full", "partial", "partial", "none", "none", "none", "none", "partial"]),
            row("guided-capture", "Guided capture for standardised inspections",
                ["full", "partial", "full", "full", "full", "full", "none", "full", "full", "full"]),
            row("fully-remote-inspection", "Fully remote inspection capability",
                ["full", "full", "full", "full", "full", "full", "none", "full", "full", "full"]),
            row("self-inspection", "Self-inspection by the end customer",
                ["full", "full", "full", "full", "full", "full", "none", "full", "full", "full"]),
            row("image-quality-checks", "Image quality checks (blur detection, validation)",
                ["full", "full", "full", "full", "full", "full", "full", "full", "full", "full"]),
        ],
    },
    {
        id: "vehicle-coverage",
        label: "Vehicle & Asset Coverage",
        items: [
            row("truck-bus-inspections", "Truck / bus inspections",
                ["full", "undisclosed", "undisclosed", "undisclosed", "undisclosed", "undisclosed", "undisclosed", "undisclosed", "undisclosed", "undisclosed"]),
            row("motorbike-inspections", "Motorbike inspections",
                ["full", "undisclosed", "undisclosed", "undisclosed", "undisclosed", "undisclosed", "undisclosed", "undisclosed", "undisclosed", "undisclosed"]),
            row("vin-license-plate", "VIN / license plate recognition",
                ["full", "partial", "full", "full", "full", "partial", "full", "partial", "full", "full"]),
        ],
    },
    {
        id: "fraud-trust",
        label: "Fraud & Trust",
        items: [
            row("basic-fraud-detection", "Basic fraud detection (pre-existing damage, image manipulation)",
                ["full", "full", "full", "full", "full", "partial", "partial", "partial", "partial", "full"]),
            row("advanced-fraud-detection", "Advanced fraud detection (vehicle swapping, hidden damage, self-inflicted damage)",
                ["full", "full", "full", "none", "full", "none", "none", "none", "none", "undisclosed"]),
            row("timestamped-audit-trail", "Timestamped audit trail (time, location)",
                ["full", "partial", "full", "full", "full", "full", "full", "full", "partial", "partial"]),
        ],
    },
    {
        id: "decisioning-estimation",
        label: "Decisioning & Estimation",
        items: [
            row("part-id-pricing", "Part identification & pricing via estimatics platforms (Mitchell, GT Motive)",
                ["full", "full", "partial", "full", "partial", "none", "partial", "partial", "full", "partial"]),
            row("repair-vs-replace", "Repair vs replace decision support",
                ["full", "full", "full", "partial", "full", "partial", "partial", "partial", "full", "full"]),
            row("total-loss-detection", "Total loss detection",
                ["full", "full", "full", "partial", "partial", "none", "partial", "none", "partial", "none"]),
            row("business-rule-filtering", "Business-rule-based damage filtering",
                ["full", "full", "partial", "full", "full", "full", "partial", "full", "full", "partial"]),
            row("automated-condition-scoring", "Automated vehicle condition scoring",
                ["full", "partial", "full", "partial", "full", "full", "full", "none", "full", "partial"]),
            row("incremental-damage-tracking", "Incremental damage tracking",
                ["full", "none", "full", "partial", "full", "full", "full", "full", "partial", "full"]),
        ],
    },
    {
        id: "reporting-analytics",
        label: "Reporting & Analytics",
        items: [
            row("decision-ready-reports", "Decision-ready inspection reports",
                ["full", "full", "full", "full", "full", "full", "full", "full", "full", "full"]),
            row("fleet-level-dashboard", "Fleet-level insights and monitoring dashboard",
                ["full", "none", "full", "partial", "partial", "full", "full", "full", "full", "partial"]),
            row("real-time-results", "Real-time inspection results",
                ["full", "full", "full", "full", "full", "full", "full", "full", "full", "full"]),
        ],
    },
    {
        id: "platform-integration",
        label: "Platform & Integration",
        items: [
            row("api-first-integration", "API-first integration into existing systems",
                ["full", "full", "full", "partial", "full", "full", "full", "full", "full", "full"]),
            row("ios-android-web", "iOS + Android + web coverage",
                ["full", "full", "full", "full", "full", "full", "full", "full", "full", "full"]),
            row("easy-adoption", "Easy adoption with minimal workflow changes",
                ["full", "partial", "full", "full", "full", "full", "none", "full", "full", "full"]),
            row("multi-language-support", "Multi-language support",
                ["full", "full", "full", "full", "full", "full", "full", "full", "partial", "full"]),
            row("workflow-automation-post-inspection", "Workflow automation post inspection",
                ["partial", "full", "full", "full", "partial", "full", "full", "full", "full", "partial"]),
            row("customisable-templates", "Customisable inspection templates",
                ["full", "undisclosed", "undisclosed", "full", "full", "undisclosed", "partial", "full", "full", "full"]),
        ],
    },
    {
        id: "deployment-fit",
        label: "Deployment & Business Fit",
        items: [
            row("hardware-free-scalability", "Hardware-free scalability (no dependency on gates)",
                ["full", "full", "full", "full", "full", "partial", "none", "full", "full", "full"]),
            row("fixed-camera-scanner-support", "Fixed camera / damage scanner support",
                ["full", "none", "full", "none", "none", "full", "full", "none", "partial", "full"]),
            row("insurance-fleet-rental-fit", "Designed for insurance, fleet, and rental use cases",
                ["full", "full", "full", "full", "full", "full", "full", "full", "partial", "full"]),
            row("focus-inspection-intelligence", "Focus on inspection intelligence and decision-making",
                ["full", "full", "full", "full", "full", "full", "full", "partial", "full", "full"]),
            row("focus-workflow-orchestration", "Focus on workflow orchestration and logistics",
                ["partial", "full", "full", "partial", "none", "full", "full", "full", "partial", "partial"]),
        ],
    },
];

export const ALL_CAPABILITIES = CAPABILITY_CATEGORIES.flatMap((cat) =>
    cat.items.map((item) => ({ ...item, category: cat.label }))
);

// ---------------------------------------------------------------------
// 2) PER-COMPETITOR PAGE COPY — from the content doc.
// ---------------------------------------------------------------------
export const COMPETITORS = {
    tractable: {
        slug: "inspektlabs-vs-tractable",
        name: "Tractable",
        primaryKeyword: "Tractable Alternative",
        meta: {
            title: "Inspektlabs vs Tractable: Compare AI Inspection Features",
            description:
                "Looking for a Tractable alternative? Compare Inspektlabs vs Tractable across AI vehicle inspection, damage detection, enterprise integrations, workflows, and deployment options.",
        },
        hero: {
            h1: "Compare Inspektlabs vs Tractable",
            copy: "Compare Inspektlabs and Tractable across AI vehicle inspection, damage detection, claims workflows, and enterprise capabilities to find the right solution for your business.",
        },
        quickPick: {
            inlineContent:
                "Both Inspektlabs and Tractable use AI to automate vehicle inspections, but they address different operational priorities. If you're looking for a Tractable alternative, this overview highlights where each platform delivers the most value before exploring the detailed comparison.",
            chooseInspektlabs: {
                heading: "You need one platform for every vehicle inspection workflow",
                bullets: [
                    "Inspect vehicles using AI-powered photos and videos",
                    "Scale inspections across insurance, fleet, rental, dealerships & auctions",
                    "Reduce fraud with AI-powered damage verification and incremental damage tracking",
                    "Deploy through mobile apps, fixed cameras or APIs",
                ],
            },
            chooseCompetitor: {
                heading: "Insurance claims are your primary workflow",
                bullets: [
                    "Claims automation is your main priority",
                    "Repair estimation is central to your process",
                    "Your teams work primarily with insurers and repair networks",
                    "Claims-centric operations are your focus",
                ],
            },
            bothPlatforms: {
                heading: "Strong capabilities in common",
                bullets: [
                    "AI vehicle damage detection",
                    "Guided inspections",
                    "Automated inspection reports",
                    "Enterprise-grade security",
                ],
            },
        },
        capabilitiesSection: {
            h2: "How Inspektlabs Compares to Tractable Across 38 Capabilities",
            builtFor: {
                inspektlabs: "Insurance, Fleet, Rental & Dealership Workflows",
                competitor: "Insurance Claims & Repair Automation",
            },
            copy: "Insurance teams evaluating Inspektlabs vs Tractable are typically comparing more than AI damage detection. They need to understand how each platform supports claims automation, repair workflows, enterprise integrations, and deployment flexibility. If you're researching a Tractable alternative, this side-by-side comparison highlights the capabilities that matter when selecting an AI vehicle inspection platform.",
        },
        gapsSection: {
            h2: "Why Businesses Choose Inspektlabs Over Tractable",
            copy: "Businesses evaluating Tractable alternatives often need more than claims automation. Inspektlabs combines AI vehicle inspection, fleet and rental workflows, enterprise integrations, and flexible deployment options in one platform, making it easier to support multiple inspection use cases.",
            cards: [
                "Video-Based Vehicle Inspections",
                "Fleet & Multi-Industry Workflows",
                "Incremental Damage Tracking",
            ],
        },
        faq: [
    {
        Q: "Can I compare Inspektlabs and Tractable before making a decision?",
        A: [
            "Yes. Many insurance companies and automotive businesses evaluate both platforms using the same vehicle inspection workflow to compare AI damage detection, inspection accuracy, reporting quality, workflow automation, and enterprise integrations. A side-by-side evaluation provides a practical way to identify the platform that best aligns with your operational requirements."
        ]
    },
    {
        Q: "What makes Inspektlabs a strong Tractable alternative?",
        A: [
            "Inspektlabs is a strong Tractable alternative for organizations that need AI vehicle inspection software beyond insurance claims automation. The platform supports insurance, fleet, rental, dealership, and automotive inspection workflows with AI damage detection, configurable workflows, enterprise integrations, and flexible deployment options from a single platform."
        ]
    },
    {
        Q: "Which platform is better for AI vehicle inspections?",
        A: [
            "The right platform depends on your business requirements. Tractable primarily focuses on insurance claims automation, while Inspektlabs supports AI vehicle inspections across multiple industries. Businesses looking to standardize inspections, automate damage assessment, and integrate with existing systems often require broader workflow capabilities."
        ]
    },
    {
        Q: "Can Inspektlabs integrate with our existing claims or fleet systems?",
        A: [
            "Yes. Inspektlabs integrates with claims management platforms, fleet management software, dealership systems, rental platforms, and enterprise applications using REST APIs and webhooks. Inspection requests, AI damage reports, images, and status updates can automatically sync with your existing workflows, reducing manual effort."
        ]
    },
    {
        Q: "How long does it take to implement Inspektlabs?",
        A: [
            "Implementation depends on the deployment model and integration requirements. Most standard deployments are completed within 2–4 weeks, while enterprise implementations receive dedicated onboarding and technical support. This enables organizations to adopt AI vehicle inspection software with minimal disruption to existing operations."
        ]
    }
]
    },

    ravin: {
        slug: "inspektlabs-vs-ravin",
        name: "Ravin",
        primaryKeyword: "Ravin Alternative",
        meta: {
            title: "Inspektlabs vs Ravin: Compare AI Inspection Software",
            description:
                "Looking for a Ravin alternative? Compare Inspektlabs vs Ravin across AI vehicle inspection, fleet workflows, damage detection, enterprise integrations, and deployment flexibility.",
        },
        hero: {
            h1: "Compare Inspektlabs vs Ravin",
            copy: "Compare Inspektlabs and Ravin across AI vehicle inspection, mobile capture, workflow automation, and enterprise capabilities to find the right solution for your business.",
        },
        quickPick: {
            inlineContent:
                "Both Inspektlabs and Ravin automate vehicle inspections using AI. If you're evaluating a Ravin alternative, this overview highlights the strengths of each platform before you explore the full comparison.",
            chooseInspektlabs: {
                heading: "You need AI inspections across the vehicle lifecycle",
                bullets: [
                    "AI-powered photo and video inspections",
                    "One platform for insurance, fleet, rental and dealerships",
                    "Fraud detection and incremental damage tracking",
                    "Flexible deployment and enterprise integrations",
                ],
            },
            chooseCompetitor: {
                heading: "Vehicle capture is your biggest priority",
                bullets: [
                    "Mobile inspections",
                    "Fixed-camera deployments",
                    "Fleet-focused inspections",
                    "High-volume capture workflows",
                ],
            },
            bothPlatforms: {
                heading: "Strong capabilities in common",
                bullets: [
                    "AI damage detection",
                    "Guided image capture",
                    "API integrations",
                    "Enterprise security",
                ],
            },
        },
        capabilitiesSection: {
            h2: "Compare Inspektlabs and Ravin Across 38 Capabilities",
            builtFor: {
                inspektlabs: "End-to-End Vehicle Inspection Workflows",
                competitor: "Mobile & Fixed-Camera Vehicle Inspections",
            },
            copy: "Companies comparing Inspektlabs and Ravin are often focused on improving inspection accuracy while reducing manual reviews. Review how both platforms support mobile inspections, fixed-camera deployments, AI damage detection, workflow automation, reporting, and enterprise integrations to determine the best fit for your operations.",
        },
        gapsSection: {
            h2: "Why Businesses Choose Inspektlabs Over Ravin",
            copy: "Organizations comparing Ravin alternatives often look beyond image capture. Inspektlabs offers flexible inspection workflows, AI damage detection, enterprise integrations, and deployment options designed for businesses operating across multiple vehicle inspection scenarios.",
            cards: [
                "Video-Based Vehicle Inspections",
                "Flexible Capture Options",
                "Fleet Insights Dashboard",
            ],
        },
        faq: [
    {
        Q: "Can I compare Inspektlabs and Ravin side by side?",
        A: [
            "Yes. Many organizations evaluate both platforms using the same inspection scenarios to compare AI damage detection, inspection accuracy, reporting, workflow automation, and enterprise integrations. Running a pilot with identical vehicle inspections helps teams make a more informed purchasing decision."
        ]
    },
    {
        Q: "What makes Inspektlabs a strong Ravin alternative?",
        A: [
            "Inspektlabs is a strong Ravin alternative for businesses looking beyond image capture alone. In addition to AI vehicle inspections, the platform supports configurable workflows, mobile and fixed-camera deployments, enterprise integrations, and inspection automation across insurance, fleet, rental, dealership, and automotive operations."
        ]
    },
    {
        Q: "Does Inspektlabs support both mobile and fixed-camera vehicle inspections?",
        A: [
            "Yes. Inspektlabs supports smartphone inspections, fixed-camera installations, inspection kiosks, and automated capture environments. Organizations can deploy the inspection workflow that best matches their operational requirements without changing their existing business processes."
        ]
    },
    {
        Q: "Can Inspektlabs integrate with existing business systems?",
        A: [
            "Yes. Inspection requests, AI-generated reports, images, and vehicle data can be exchanged with claims systems, fleet management platforms, CRMs, ERPs, and internal applications through secure REST APIs and webhooks, helping automate inspection workflows from end to end."
        ]
    },
    {
        Q: "How quickly can Inspektlabs be deployed?",
        A: [
            "Most implementations are completed within 2–4 weeks, depending on workflow complexity and integration requirements. Enterprise customers receive dedicated implementation support to accelerate deployment while ensuring a smooth transition into production environments."
        ]
    }
]
    },

    bdeo: {
        slug: "inspektlabs-vs-bdeo",
        name: "Bdeo",
        primaryKeyword: "Bdeo Alternative",
        meta: {
            title: "Inspektlabs vs Bdeo: Compare Insurance Inspection AI",
            description:
                "Looking for a Bdeo alternative? Compare Inspektlabs vs Bdeo across AI vehicle inspection, claims automation, fraud detection, underwriting, and enterprise integrations.",
        },
        hero: {
            h1: "Compare Inspektlabs vs Bdeo",
            copy: "Compare Inspektlabs and Bdeo across AI vehicle inspection, claims automation, fraud detection, and enterprise capabilities to evaluate the right platform for your organization.",
        },
        quickPick: {
            inlineContent:
                "Both Inspektlabs and Bdeo apply AI to vehicle inspections and insurance operations. If you're researching a Bdeo alternative, this overview shows where each platform fits before the detailed comparison.",
            chooseInspektlabs: {
                heading: "You need AI inspections beyond insurance claims",
                bullets: [
                    "AI-powered inspections using photos and videos",
                    "Fleet, rental and dealership workflows",
                    "Flexible deployment across multiple business models",
                    "Advanced fraud detection and remote inspections",
                ],
            },
            chooseCompetitor: {
                heading: "Insurance automation is your main requirement",
                bullets: [
                    "Claims processing",
                    "Underwriting support",
                    "Insurance fraud workflows",
                    "Insurance-first implementation",
                ],
            },
            bothPlatforms: {
                heading: "Strong capabilities in common",
                bullets: [
                    "AI damage detection",
                    "Claims automation support",
                    "Enterprise APIs",
                    "Security and compliance",
                ],
            },
        },
        capabilitiesSection: {
            h2: "Compare Inspektlabs and Bdeo Across 38 Capabilities",
            builtFor: {
                inspektlabs: "Vehicle Inspection Across Multiple Industries",
                competitor: "Insurance Claims Automation & Fraud Detection",
            },
            copy: "Insurance providers evaluating Inspektlabs and Bdeo typically compare how each platform supports claims automation, fraud detection, underwriting, and vehicle damage assessment. This comparison helps you understand the differences in AI inspection capabilities, deployment flexibility, and enterprise integrations.",
        },
        gapsSection: {
            h2: "Why Businesses Choose Inspektlabs Over Bdeo",
            copy: "Companies researching Bdeo alternatives often require more than insurance claims automation. Inspektlabs supports vehicle inspections across insurance, fleet, rental, dealership, and automotive operations through a single AI-powered platform.",
            cards: [
                "Multi-Industry Inspection Workflows",
                "Video-Based Damage Assessment",
                "Historical Damage Tracking",
            ],
        },
        faq: [
    {
        Q: "Can I compare Inspektlabs and Bdeo before choosing a platform?",
        A: [
            "Yes. Many insurers evaluate both platforms using the same vehicle inspection workflow to compare AI damage detection, claims automation, fraud detection, reporting accuracy, and enterprise integrations. A structured evaluation helps determine which solution best supports your operational goals."
        ]
    },
    {
        Q: "What makes Inspektlabs a strong Bdeo alternative?",
        A: [
            "Inspektlabs is a strong Bdeo alternative for organizations that require AI vehicle inspection software across multiple business workflows. In addition to insurance claims, the platform supports fleet inspections, rental operations, dealership inspections, vehicle auctions, and automotive inspection workflows through a single enterprise platform."
        ]
    },
    {
        Q: "Does Inspektlabs support AI vehicle inspections beyond insurance claims?",
        A: [
            "Yes. Inspektlabs is designed for insurance, fleet, rental, dealership, and automotive businesses. AI-powered inspections can be used throughout the vehicle lifecycle, including underwriting, claims, maintenance, trade-ins, check-in/check-out, remarketing, and condition reporting."
        ]
    },
    {
        Q: "Can Inspektlabs integrate with claims management systems?",
        A: [
            "Yes. Inspektlabs integrates with claims platforms, policy administration systems, CRMs, enterprise applications, and internal workflows using REST APIs and webhooks. Inspection reports and AI damage assessments can automatically flow into your existing business systems."
        ]
    },
    {
        Q: "What is the typical implementation timeline?",
        A: [
            "Typical implementations take 2–4 weeks, depending on deployment requirements and enterprise integrations. Dedicated onboarding and technical support help organizations deploy AI vehicle inspection software quickly while minimizing operational disruption."
        ]
    }
]
    },

    tchek: {
        slug: "inspektlabs-vs-tchek",
        name: "Tchek",
        primaryKeyword: "Tchek Alternative",
        meta: {
            title: "Inspektlabs vs Tchek: Compare Inspection Features",
            description:
                "Looking for a Tchek alternative? Compare Inspektlabs vs Tchek across AI vehicle inspection, dealership workflows, damage detection, enterprise integrations, and reporting.",
        },
        hero: {
            h1: "Compare Inspektlabs vs Tchek",
            copy: "Compare Inspektlabs and Tchek across AI vehicle inspection, dealership workflows, pricing integrations, and enterprise capabilities to choose the right platform for your business.",
        },
        quickPick: {
            inlineContent:
                "Both Inspektlabs and Tchek help organizations digitize vehicle inspections. If you're considering a Tchek alternative, this overview compares where each platform is strongest before the full feature comparison.",
            chooseInspektlabs: {
                heading: "You need an enterprise inspection platform",
                bullets: [
                    "End-to-end vehicle inspection workflows",
                    "AI-powered photo and video inspections",
                    "Fleet dashboard with analytics and reporting",
                    "Fraud detection across multiple industries",
                ],
            },
            chooseCompetitor: {
                heading: "Dealership operations are your primary use case",
                bullets: [
                    "Vehicle appraisals",
                    "Pricing integrations",
                    "Dealership inspections",
                    "Logistics workflows",
                ],
            },
            bothPlatforms: {
                heading: "Strong capabilities in common",
                bullets: [
                    "AI damage detection",
                    "Guided inspections",
                    "Inspection reporting",
                    "Enterprise APIs",
                ],
            },
        },
        capabilitiesSection: {
            h2: "Compare Inspektlabs and Tchek Across 38 Capabilities",
            builtFor: {
                inspektlabs: "Enterprise Vehicle Inspection Workflows",
                competitor: "Dealership & Logistics Inspections",
            },
            copy: "Dealerships and logistics providers comparing Inspektlabs and Tchek often prioritize inspection consistency, pricing workflows, and operational efficiency. Explore how both platforms support AI damage detection, dealership inspections, workflow automation, reporting, and business system integrations.",
        },
        gapsSection: {
            h2: "Why Businesses Choose Inspektlabs Over Tchek",
            copy: "Businesses evaluating Tchek alternatives often compare inspection workflows, deployment flexibility, and operational visibility. Inspektlabs helps streamline inspections while integrating with existing enterprise systems.",
            cards: [
                "Video Inspection Workflows",
                "Fleet Performance Dashboard",
                "AI Fraud Detection",
            ],
        },
        faq: [
    {
        Q: "Can I evaluate Inspektlabs and Tchek before choosing a platform?",
        A: [
            "Yes. Many dealerships, fleet operators, and automotive businesses compare both platforms using the same vehicle inspection process. Evaluating AI damage detection, reporting accuracy, workflow automation, and integrations side by side helps determine which platform best supports your operational requirements."
        ]
    },
    {
        Q: "What makes Inspektlabs a strong Tchek alternative?",
        A: [
            "Inspektlabs is a strong Tchek alternative for organizations looking beyond dealership inspections. The platform supports AI vehicle inspections across insurance, fleet, rental, dealership, auctions, and automotive workflows with enterprise integrations, configurable workflows, and flexible deployment options."
        ]
    },
    {
        Q: "Is Inspektlabs suitable for dealership vehicle inspections?",
        A: [
            "Yes. Inspektlabs supports dealership inspections for trade-ins, inventory management, service intake, used vehicle assessments, and vehicle condition reporting. AI-powered damage detection helps dealerships standardize inspections, improve consistency, and reduce manual review time."
        ]
    },
    {
        Q: "Can Inspektlabs integrate with dealership management systems?",
        A: [
            "Yes. Inspektlabs integrates with dealership management systems (DMS), CRMs, ERP platforms, and other enterprise applications through REST APIs and webhooks. Inspection reports and vehicle data can automatically sync with your existing business systems."
        ]
    },
    {
        Q: "How long does it take to deploy Inspektlabs?",
        A: [
            "Most deployments are completed within 2–4 weeks, depending on your inspection workflow and integration requirements. Enterprise customers receive dedicated implementation support to ensure a smooth rollout with minimal disruption to business operations."
        ]
    }
]
    },

    focalx: {
        slug: "inspektlabs-vs-focalx",
        name: "FocalX",
        primaryKeyword: "FocalX Alternative",
        meta: {
            title: "Inspektlabs vs FocalX: Compare Fleet Inspection Software",
            description:
                "Looking for a FocalX alternative? Compare Inspektlabs vs FocalX across fleet inspections, rental workflows, AI damage detection, enterprise integrations, and deployment options.",
        },
        hero: {
            h1: "Compare Inspektlabs vs FocalX",
            copy: "Compare Inspektlabs and FocalX across fleet inspections, rental workflows, AI damage detection, and enterprise capabilities to choose the right inspection platform.",
        },
        quickPick: {
            inlineContent:
                "Both Inspektlabs and FocalX automate vehicle inspections with AI. If you're looking for a FocalX alternative, this overview highlights where each platform adds the most value before the detailed comparison.",
            chooseInspektlabs: {
                heading: "You need one platform across multiple operations",
                bullets: [
                    "Insurance, fleet, rental and dealership inspections",
                    "AI-powered photo and video inspections",
                    "Fraud detection and damage verification",
                    "Fleet dashboard and enterprise integrations",
                ],
            },
            chooseCompetitor: {
                heading: "Fleet and rental inspections are your primary focus",
                bullets: [
                    "Fleet operations",
                    "Rental workflows",
                    "Mobile inspections",
                    "Logistics processes",
                ],
            },
            bothPlatforms: {
                heading: "Strong capabilities in common",
                bullets: [
                    "AI vehicle inspections",
                    "Damage detection",
                    "Inspection reporting",
                    "Remote inspections",
                ],
            },
        },
        capabilitiesSection: {
            h2: "Compare Inspektlabs and FocalX Across 38 Capabilities",
            builtFor: {
                inspektlabs: "Insurance, Fleet & Dealership Operations",
                competitor: "Fleet & Rental Inspection Workflows",
            },
            copy: "Fleet operators and rental businesses evaluating Inspektlabs and FocalX are usually looking to modernize inspections without slowing vehicle turnaround. Compare how both platforms support fleet workflows, rental operations, AI damage detection, deployment flexibility, reporting, and enterprise integrations.",
        },
        gapsSection: {
            h2: "Why Businesses Choose Inspektlabs Over FocalX",
            copy: "Organizations considering FocalX alternatives often need broader workflow automation alongside fleet inspections. Inspektlabs combines AI damage detection, insurance-ready workflows, and enterprise integrations within a single platform.",
            cards: [
                "Insurance-Ready AI Damage Detection",
                "AI Fraud Detection",
                "Incremental Damage Tracking",
            ],
        },
        faq: [
    {
        Q: "Can I compare Inspektlabs and FocalX during a pilot?",
        A: [
            "Yes. Fleet operators and rental businesses frequently evaluate both platforms using real inspection workflows. Comparing AI damage detection, reporting, workflow automation, and deployment flexibility helps organizations identify the platform that best fits their operational needs."
        ]
    },
    {
        Q: "What makes Inspektlabs a strong FocalX alternative?",
        A: [
            "Inspektlabs is a strong FocalX alternative for organizations that need AI vehicle inspection software across multiple industries. Beyond fleet inspections, the platform supports insurance, rental, dealership, auctions, and automotive workflows through configurable AI-powered inspection processes."
        ]
    },
    {
        Q: "Does Inspektlabs support fleet and rental vehicle inspections?",
        A: [
            "Yes. Inspektlabs automates inspections throughout the vehicle lifecycle, including vehicle check-in, check-out, maintenance, lease returns, and remarketing. AI-generated inspection reports help fleet and rental businesses improve operational efficiency and reduce manual inspections."
        ]
    },
    {
        Q: "Can Inspektlabs integrate with fleet management software?",
        A: [
            "Yes. Inspektlabs connects with fleet management platforms, rental management systems, CRMs, ERPs, and enterprise applications using REST APIs and webhooks. Inspection reports and AI damage assessments can be automatically shared across your existing workflows."
        ]
    },
    {
        Q: "How quickly can Inspektlabs be implemented?",
        A: [
            "Most implementations are completed within 2–4 weeks, depending on deployment complexity and integration requirements. Dedicated onboarding helps organizations deploy AI vehicle inspection software quickly while minimizing operational disruption."
        ]
    }
]
    },

    uveye: {
        slug: "inspektlabs-vs-uveye",
        name: "UVeye",
        primaryKeyword: "UVeye Alternative",
        meta: {
            title: "Inspektlabs vs UVeye: Compare Inspection Solutions",
            description:
                "Looking for a UVeye alternative? Compare Inspektlabs vs UVeye across AI vehicle inspection, drive-through scanning, deployment models, enterprise integrations, and workflow automation.",
        },
        hero: {
            h1: "Compare Inspektlabs vs UVeye",
            copy: "Compare Inspektlabs and UVeye across deployment models, AI vehicle inspection, workflow automation, and enterprise capabilities to identify the best fit for your operations.",
        },
        quickPick: {
            inlineContent:
                "Both Inspektlabs and UVeye automate vehicle inspections using AI, but with different deployment approaches. If you're evaluating a UVeye alternative, this overview highlights where each solution fits best before the full comparison.",
            chooseInspektlabs: {
                heading: "You need flexible AI inspections without hardware limitations",
                bullets: [
                    "Mobile, fixed-camera and API deployments",
                    "AI-powered photo and video inspections",
                    "Fraud detection and incremental damage tracking",
                    "One platform across multiple industries",
                ],
            },
            chooseCompetitor: {
                heading: "High-volume drive-through inspections are your priority",
                bullets: [
                    "Automated inspection tunnels",
                    "Fixed hardware deployments",
                    "High-throughput vehicle scanning",
                    "Infrastructure-based inspections",
                ],
            },
            bothPlatforms: {
                heading: "Strong capabilities in common",
                bullets: [
                    "AI damage detection",
                    "Inspection reports",
                    "Enterprise security",
                    "API integrations",
                ],
            },
        },
        capabilitiesSection: {
            h2: "Compare Inspektlabs and UVeye Across 38 Capabilities",
            builtFor: {
                inspektlabs: "Flexible Mobile & Fixed-Camera Deployments",
                competitor: "Drive-Through Vehicle Inspection Systems",
            },
            copy: "Hardware-based inspection systems and software-first platforms solve different operational challenges. If you're comparing Inspektlabs and UVeye, explore how each solution approaches drive-through scanning, AI damage detection, deployment models, workflow automation, reporting, and enterprise connectivity.",
        },
        gapsSection: {
            h2: "Why Businesses Choose Inspektlabs Over UVeye",
            copy: "Businesses looking for a UVeye alternative often compare deployment flexibility and infrastructure requirements. Inspektlabs supports mobile, fixed-camera, and remote vehicle inspections without requiring dedicated scanning hardware.",
            cards: [
                "Hardware-Free Deployment",
                "Remote Vehicle Inspections",
                "Enterprise Workflow Integrations",
            ],
        },
        faq: [
    {
        Q: "Can I compare Inspektlabs and UVeye before making a purchase?",
        A: [
            "Yes. Many organizations evaluate both platforms to compare AI vehicle inspection accuracy, deployment models, reporting capabilities, workflow automation, and enterprise integrations. A side-by-side evaluation helps determine which solution best aligns with your inspection operations."
        ]
    },
    {
        Q: "What makes Inspektlabs a strong UVeye alternative?",
        A: [
            "Inspektlabs is a strong UVeye alternative for businesses seeking flexible deployment options. The platform supports smartphone, fixed-camera, and remote AI vehicle inspections without requiring dedicated drive-through scanning infrastructure, making it suitable for a wider range of operational environments."
        ]
    },
    {
        Q: "Do I need dedicated hardware to use Inspektlabs?",
        A: [
            "No. Inspektlabs supports mobile devices, fixed-camera installations, and existing camera infrastructure, allowing organizations to deploy AI vehicle inspections without investing in specialized inspection hardware. This provides greater flexibility across different inspection workflows."
        ]
    },
    {
        Q: "Can Inspektlabs integrate with our existing business systems?",
        A: [
            "Yes. Inspektlabs integrates with claims platforms, fleet management systems, dealership software, rental applications, and enterprise systems using REST APIs and webhooks. Inspection data and AI-generated reports can flow directly into your existing business processes."
        ]
    },
    {
        Q: "How long does enterprise deployment take?",
        A: [
            "Standard deployments are typically completed within 2–4 weeks, while larger enterprise implementations may vary depending on integration scope. Dedicated implementation and technical support help ensure a successful rollout with minimal disruption."
        ]
    }
]
    },

    weproov: {
        slug: "inspektlabs-vs-weproov",
        name: "WeProov",
        primaryKeyword: "WeProov Alternative",
        meta: {
            title: "Inspektlabs vs WeProov: Compare Digital Inspections",
            description:
                "Looking for a WeProov alternative? Compare Inspektlabs vs WeProov across digital vehicle inspections, proof-of-condition workflows, AI damage detection, and enterprise integrations.",
        },
        hero: {
            h1: "Compare Inspektlabs vs WeProov",
            copy: "Compare Inspektlabs and WeProov across proof-of-condition inspections, AI damage detection, rental workflows, and enterprise capabilities to find the right solution for your business.",
        },
        quickPick: {
            inlineContent:
                "Both Inspektlabs and WeProov digitize vehicle inspections. If you're researching a WeProov alternative, this overview compares where each platform performs best before you explore the detailed comparison.",
            chooseInspektlabs: {
                heading: "You need AI-powered inspection automation",
                bullets: [
                    "AI-powered photo and video inspections",
                    "Insurance, fleet and rental workflows",
                    "Fraud detection and damage verification",
                    "Flexible enterprise deployment",
                ],
            },
            chooseCompetitor: {
                heading: "Proof of condition is your primary requirement",
                bullets: [
                    "Vehicle handovers",
                    "Rental inspections",
                    "Digital condition reports",
                    "Customer check-in and check-out",
                ],
            },
            bothPlatforms: {
                heading: "Strong capabilities in common",
                bullets: [
                    "Digital inspections",
                    "Inspection reports",
                    "Remote inspections",
                    "Enterprise security",
                ],
            },
        },
        capabilitiesSection: {
            h2: "Compare Inspektlabs and WeProov Across 38 Capabilities",
            builtFor: {
                inspektlabs: "AI-Powered Vehicle Inspection Platform",
                competitor: "Digital Proof of Condition",
            },
            copy: "Digital proof of condition is only one part of the inspection process. Organizations comparing Inspektlabs and WeProov often evaluate inspection accuracy, rental workflows, AI damage detection, reporting, and workflow flexibility before choosing a vehicle inspection platform.",
        },
        gapsSection: {
            h2: "Why Businesses Choose Inspektlabs Over WeProov",
            copy: "Organizations evaluating WeProov alternatives often require more than proof-of-condition reports. Inspektlabs combines AI damage detection, workflow automation, enterprise integrations, and reporting to support complete vehicle inspection workflows.",
            cards: [
                "AI Damage Detection",
                "Fleet Analytics Dashboard",
                "Video-Based Vehicle Inspections",
            ],
        },
        faq: [
    {
        Q: "Can I compare Inspektlabs and WeProov before choosing a platform?",
        A: [
            "Yes. Many rental companies, fleet operators, and automotive businesses compare both platforms using the same vehicle inspection workflow. Evaluating AI damage detection, reporting, workflow automation, and enterprise integrations side by side helps identify the platform that best supports your operational requirements."
        ]
    },
    {
        Q: "What makes Inspektlabs a strong WeProov alternative?",
        A: [
            "Inspektlabs is a strong WeProov alternative for organizations that need more than digital proof of condition. The platform combines AI vehicle inspection, automated damage detection, configurable workflows, enterprise integrations, and reporting to support insurance, fleet, rental, dealership, and automotive operations."
        ]
    },
    {
        Q: "Does Inspektlabs do more than proof-of-condition reporting?",
        A: [
            "Yes. In addition to digital proof of condition, Inspektlabs automatically detects vehicle damage, classifies defects, generates AI inspection reports, and integrates inspection results into your existing business systems. This helps reduce manual reviews and accelerate vehicle inspection workflows."
        ]
    },
    {
        Q: "Can Inspektlabs automate rental vehicle inspections?",
        A: [
            "Yes. Inspektlabs supports rental vehicle check-in, check-out, lease returns, and vehicle condition reporting through AI-powered inspection workflows. Inspection reports are generated automatically, helping rental businesses improve turnaround times and maintain consistent documentation."
        ]
    },
    {
        Q: "How long does it take to implement Inspektlabs?",
        A: [
            "Most implementations are completed within 2–4 weeks, depending on your workflow configuration and integration requirements. Enterprise customers receive dedicated onboarding and implementation support to ensure a smooth deployment with minimal operational disruption."
        ]
    }
]
    },

    pave: {
        slug: "inspektlabs-vs-pave",
        name: "PAVE",
        primaryKeyword: "PAVE Alternative",
        meta: {
            title: "Inspektlabs vs PAVE: Compare Mobile Inspections",
            description:
                "Looking for a PAVE alternative? Compare Inspektlabs vs PAVE across mobile vehicle inspections, dealership workflows, AI damage detection, enterprise integrations, and reporting.",
        },
        hero: {
            h1: "Compare Inspektlabs vs PAVE",
            copy: "Compare Inspektlabs and PAVE across smartphone vehicle inspections, dealership workflows, AI damage detection, and enterprise capabilities to evaluate the right inspection platform.",
        },
        quickPick: {
            inlineContent:
                "Both Inspektlabs and PAVE simplify vehicle inspections using AI. If you're considering a PAVE alternative, this overview highlights the strengths of each platform before the detailed comparison.",
            chooseInspektlabs: {
                heading: "You need enterprise inspection workflows",
                bullets: [
                    "AI-powered inspections using photos and videos",
                    "Insurance, fleet and rental workflows",
                    "Fraud detection and damage verification",
                    "Fleet dashboard and enterprise integrations",
                ],
            },
            chooseCompetitor: {
                heading: "Smartphone inspections are your main use case",
                bullets: [
                    "Mobile capture",
                    "Guided inspections",
                    "Dealership workflows",
                    "Condition reporting",
                ],
            },
            bothPlatforms: {
                heading: "Strong capabilities in common",
                bullets: [
                    "AI damage detection",
                    "Guided inspections",
                    "Inspection reports",
                    "Enterprise security",
                ],
            },
        },
        capabilitiesSection: {
            h2: "Compare Inspektlabs and PAVE Across 38 Capabilities",
            builtFor: {
                inspektlabs: "Multi-Industry Inspection Workflows",
                competitor: "Smartphone Vehicle Inspections",
            },
            copy: "Organizations modernizing dealership inspections often compare Inspektlabs and PAVE to understand how each platform supports smartphone-based inspections, AI damage detection, workflow automation, enterprise deployments, and reporting capabilities.",
        },
        gapsSection: {
            h2: "Why Businesses Choose Inspektlabs Over PAVE",
            copy: "Companies researching PAVE alternatives often compare smartphone inspection capabilities alongside enterprise workflow automation. Inspektlabs delivers AI-powered inspections, reporting, and integrations for businesses operating at scale.",
            cards: [
                "Enterprise Workflow Automation",
                "AI Fraud Detection",
                "Fleet Dashboard",
            ],
        },
        faq: [
    {
        Q: "Can I compare Inspektlabs and PAVE before making a decision?",
        A: [
            "Yes. Many dealerships, fleet operators, and automotive businesses evaluate both platforms using the same inspection scenarios. Comparing AI damage detection, inspection accuracy, reporting, workflow automation, and enterprise integrations provides a clear understanding of which platform best meets your business needs."
        ]
    },
    {
        Q: "What makes Inspektlabs a strong PAVE alternative?",
        A: [
            "Inspektlabs is a strong PAVE alternative for organizations looking beyond smartphone inspections. The platform combines AI vehicle inspection, configurable workflows, enterprise integrations, reporting, and flexible deployment options to support inspection operations across multiple industries."
        ]
    },
    {
        Q: "Which platform is better for smartphone vehicle inspections?",
        A: [
            "Both platforms support smartphone-based inspections, but the right choice depends on your operational requirements. Inspektlabs extends smartphone inspections with AI damage detection, workflow automation, enterprise reporting, and integrations for insurance, fleet, rental, dealership, and automotive businesses."
        ]
    },
    {
        Q: "Can Inspektlabs integrate with dealership and enterprise systems?",
        A: [
            "Yes. Inspektlabs integrates with dealership management systems, fleet platforms, CRMs, ERP software, and enterprise applications through REST APIs and webhooks. Inspection reports and vehicle data can be automatically shared across your existing workflows."
        ]
    },
    {
        Q: "How quickly can Inspektlabs be deployed?",
        A: [
            "Standard deployments are typically completed within 2–4 weeks, depending on the complexity of your inspection workflow and integrations. Dedicated implementation support helps organizations deploy AI vehicle inspection software efficiently and at scale."
        ]
    }
]
    },

    monkai: {
        slug: "inspektlabs-vs-monk-ai",
        name: "Monk AI",
        primaryKeyword: "Monk AI Alternative",
        meta: {
            title: "Inspektlabs vs Monk AI: Compare Damage Detection AI",
            description:
                "Looking for a Monk AI alternative? Compare Inspektlabs vs Monk AI across AI damage detection, computer vision, embedded APIs, enterprise integrations, and inspection workflows.",
        },
        hero: {
            h1: "Compare Inspektlabs vs Monk AI",
            copy: "Compare Inspektlabs and Monk AI across AI damage detection, computer vision APIs, inspection workflows, and enterprise capabilities to choose the right solution for your business.",
        },
        quickPick: {
            inlineContent:
                "Both Inspektlabs and Monk AI use computer vision to automate vehicle damage assessment. If you're evaluating a Monk AI alternative, this overview highlights where each platform differs before the detailed comparison.",
            chooseInspektlabs: {
                heading: "You need a complete inspection platform\u2014not just AI models",
                bullets: [
                    "AI-powered photo and video inspections",
                    "Fleet dashboard and operational reporting",
                    "Fraud detection and incremental damage tracking",
                    "Insurance, fleet, rental and dealership workflows",
                ],
            },
            chooseCompetitor: {
                heading: "Embedded AI is your primary requirement",
                bullets: [
                    "Computer vision APIs",
                    "SDK integrations",
                    "Embedded damage detection",
                    "Developer-first implementation",
                ],
            },
            bothPlatforms: {
                heading: "Strong capabilities in common",
                bullets: [
                    "AI damage detection",
                    "Enterprise APIs",
                    "Security and compliance",
                    "Cloud deployment",
                ],
            },
        },
        capabilitiesSection: {
            h2: "Compare Inspektlabs and Monk AI Across 38 Capabilities",
            builtFor: {
                inspektlabs: "Complete Vehicle Inspection Platform",
                competitor: "Embedded AI Damage Detection APIs",
            },
            copy: "If your team is evaluating Inspektlabs and Monk AI, APIs are only part of the decision. Compare embedded AI capabilities, computer vision, damage detection, deployment options, enterprise integrations, reporting, and workflow flexibility to identify the platform that best fits your requirements.",
        },
        gapsSection: {
            h2: "Why Businesses Choose Inspektlabs Over Monk AI",
            copy: "Businesses comparing Monk AI alternatives often need more than embedded AI APIs. Inspektlabs provides a complete AI vehicle inspection platform with guided capture, reporting, enterprise integrations, and deployment flexibility.",
            cards: [
                "Complete Inspection Platform",
                "Video-Based Vehicle Inspections",
                "Fleet Insights Dashboard",
            ],
        },
        faq: [
    {
        Q: "Can I compare Inspektlabs and Monk AI during an evaluation?",
        A: [
            "Yes. Organizations evaluating AI vehicle inspection platforms often compare both solutions using the same inspection workflows. Assessing AI damage detection, reporting, workflow automation, deployment options, and enterprise integrations helps identify the platform that best supports long-term business requirements."
        ]
    },
    {
        Q: "What makes Inspektlabs a strong Monk AI alternative?",
        A: [
            "Inspektlabs is a strong Monk AI alternative for businesses that need a complete AI vehicle inspection platform rather than standalone computer vision APIs. The platform combines guided inspections, AI damage detection, reporting, workflow automation, and enterprise integrations in a single solution."
        ]
    },
    {
        Q: "Does Inspektlabs provide more than AI damage detection APIs?",
        A: [
            "Yes. In addition to AI damage detection, Inspektlabs provides end-to-end vehicle inspection workflows, guided image capture, automated inspection reports, configurable business rules, analytics, and enterprise integrations. This enables organizations to automate the entire inspection process rather than individual AI functions."
        ]
    },
    {
        Q: "Can Inspektlabs integrate with enterprise applications?",
        A: [
            "Yes. Inspektlabs connects with claims management platforms, fleet management software, dealership systems, CRMs, ERP platforms, and other enterprise applications using REST APIs and webhooks. Inspection data can be automatically synchronized across your existing technology ecosystem."
        ]
    },
    {
        Q: "How long does enterprise implementation take?",
        A: [
            "Most enterprise implementations are completed within 2–4 weeks, depending on deployment requirements and system integrations. Dedicated implementation specialists work closely with your team to ensure a successful rollout and faster time to value."
        ]
    }
]
    },
};

// Shared, page-agnostic closing CTA copy (same on every comparison page).
export const CLOSING_CTA = {
    eyebrow: "Get started",
    title: "Ready to Evaluate Inspektlabs?",
    body: "Whether you're comparing vehicle inspection software for insurance, fleet, rental, dealership, or automotive operations, our team can help you evaluate the right solution for your workflow, integrations, and deployment requirements.",
    cta: "Request a Demo",
};

export function getAllCompetitorKeys() {
    return COMPETITOR_ORDER;
}

export function getComparisonContent(competitorKey) {
    const competitor = COMPETITORS[competitorKey];
    if (!competitor) return null;
    return { key: competitorKey, ...competitor };
}